import { useEffect, useState } from 'react';
import { CopyButton } from '../common/CopyButton';
import { Badge } from '../common/Badge';
import { Tooltip } from '../common/Tooltip';
import { Logo } from '../common/Logo';
import { formatRoomCode, formatDuration } from '../../utils/format';

interface MeetingHeaderProps {
  roomName: string;
  roomCode: string;
  participantCount: number;
  maxParticipants: number;
  isHost: boolean;
  startTime: Date;
  onPopoutParticipants?: () => void;
  onPopoutScreenShare?: () => void;
  hasScreenShare?: boolean;
}

/**
 * Meeting room header with room info and controls.
 */
export function MeetingHeader({
  roomName,
  roomCode,
  participantCount,
  maxParticipants,
  isHost,
  startTime,
  onPopoutParticipants,
  onPopoutScreenShare,
  hasScreenShare,
}: MeetingHeaderProps) {
  const formattedCode = formatRoomCode(roomCode);

  return (
    <header className="flex items-center justify-between px-6 py-4 bg-surface/80 backdrop-blur-lg border-b border-border">
      <div className="flex items-center gap-4">
        <Logo size="sm" showWordmark={false} />
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-semibold text-lg">{roomName}</h1>
            {isHost && <Badge variant="primary">Host</Badge>}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span className="px-2 py-1 rounded-full bg-white/5 border border-border">
              {formattedCode}
            </span>
            <CopyButton text={roomCode} label="Copy room code" />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {onPopoutParticipants && (
          <Tooltip content="Open participants in new window">
            <button
              onClick={onPopoutParticipants}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <PopoutIcon />
            </button>
          </Tooltip>
        )}

        {hasScreenShare && onPopoutScreenShare && (
          <Tooltip content="Open screen share in new window">
            <button
              onClick={onPopoutScreenShare}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <ScreenShareIcon />
            </button>
          </Tooltip>
        )}

        <Tooltip content={`${participantCount} of ${maxParticipants} participants`}>
          <div className="flex items-center gap-2 text-gray-400">
            <ParticipantsIcon />
            <span>{participantCount}/{maxParticipants}</span>
          </div>
        </Tooltip>

        <MeetingTimer startTime={startTime} />
      </div>
    </header>
  );
}

interface MeetingTimerProps {
  startTime: Date;
}

/**
 * Displays elapsed meeting time.
 */
function MeetingTimer({ startTime }: MeetingTimerProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const seconds = Math.floor((Date.now() - startTime.getTime()) / 1000);
      setElapsed(seconds);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <div className="flex items-center gap-2 text-gray-400">
      <ClockIcon />
      <span className="font-mono">{formatDuration(elapsed)}</span>
    </div>
  );
}

/**
 * Icon for participant count.
 */
function ParticipantsIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
    </svg>
  );
}

/**
 * Icon for the meeting timer.
 */
function ClockIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Icon for pop-out window button.
 */
function PopoutIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}

/**
 * Icon for screen share pop-out button.
 */
function ScreenShareIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}
